import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CircleDollarSign,
  Database,
  Factory,
  Landmark,
  LockKeyhole,
  MousePointerClick,
  Network,
  ShieldCheck,
  Workflow,
  Zap,
  type LucideIcon
} from 'lucide-react';

import Footer from '@/shared/components/Footer';
import Navbar from '@/shared/components/Navbar';
import { Badge } from '@/shared/components/ui/Badge';
import { ButtonArrow } from '@/shared/components/ui/ButtonArrow';
import { Button } from '@/shared/components/ui/Button';
import { useI18n } from '@/shared/contexts/I18nProvider';
import { cn, scrollToElementId } from '@/shared/utils';

const caseCardVisuals: readonly {
  icon: LucideIcon;
  logoAlt?: string;
  logoClassName?: string;
  logoSrc?: string;
}[] = [
  {
    icon: CircleDollarSign,
    logoAlt: 'Inspira Grupa logo',
    logoClassName: 'max-h-40 px-5 sm:max-h-44',
    logoSrc: '/images/inspira.png'
  },
  {
    icon: Factory,
    logoAlt: 'Cropt logo',
    logoClassName: 'max-h-48 scale-[1.22] px-1 sm:scale-[1.34]',
    logoSrc: '/images/cropt.png'
  },
  {
    icon: Network
  },
  {
    icon: Landmark
  }
];

const operatingPatternIcons = [
  CircleDollarSign,
  ShieldCheck,
  LockKeyhole
] as const;
const proofSelectorIcons = operatingPatternIcons;
const proofVisuals = [
  {
    alt: 'DataSliv cost optimization interface',
    darkSrc: '/images/cost_dark.png',
    lightSrc: '/images/cost_light.png'
  },
  {
    alt: 'DataSliv control interface',
    darkSrc: '/images/control_dark.png',
    lightSrc: '/images/control_light.png'
  },
  {
    alt: 'DataSliv scale interface',
    darkSrc: '/images/scale_dark.png',
    lightSrc: '/images/scale_light.png'
  }
] as const;
const proofTimerDurationMs = 4200;
const capabilityIcons = [
  Workflow,
  Database,
  ShieldCheck,
  Zap,
  Network
] as const;

const CasesPage = () => {
  const { content, localizedPath } = useI18n();
  const cases = content.cases;
  const [activeProofIndex, setActiveProofIndex] = useState(0);
  const [proofProgress, setProofProgress] = useState(0);
  const [isProofPaused, setIsProofPaused] = useState(false);
  const proofProgressRef = useRef(0);
  const caseCards = cases.caseCards.map((card, index) => ({
    ...card,
    visual: caseCardVisuals[index] ?? caseCardVisuals[0]
  }));
  const proofSelectorItems = cases.outcomeOverview.cards.map((card, index) => ({
    ...card,
    icon: proofSelectorIcons[index] ?? proofSelectorIcons[0],
    visual: proofVisuals[index] ?? proofVisuals[0]
  }));
  const activeProofItem =
    proofSelectorItems[activeProofIndex] ?? proofSelectorItems[0];
  const ActiveProofIcon = activeProofItem?.icon ?? proofSelectorIcons[0];
  const activeProofVisual = activeProofItem?.visual ?? proofVisuals[0];

  useEffect(() => {
    if (isProofPaused || proofSelectorItems.length <= 1) {
      return undefined;
    }

    let frameId = 0;
    let previousFrameTime = performance.now();

    const advanceProofTimer = (frameTime: number) => {
      const elapsedMs = frameTime - previousFrameTime;
      previousFrameTime = frameTime;

      const nextProgress =
        proofProgressRef.current + elapsedMs / proofTimerDurationMs;

      if (nextProgress >= 1) {
        proofProgressRef.current = 0;
        setProofProgress(0);
        setActiveProofIndex(
          currentIndex => (currentIndex + 1) % proofSelectorItems.length
        );
      } else {
        proofProgressRef.current = nextProgress;
        setProofProgress(nextProgress);
      }

      frameId = window.requestAnimationFrame(advanceProofTimer);
    };

    frameId = window.requestAnimationFrame(advanceProofTimer);

    return () => window.cancelAnimationFrame(frameId);
  }, [isProofPaused, proofSelectorItems.length]);

  const handleProofSelect = (index: number) => {
    proofProgressRef.current = 0;
    setActiveProofIndex(index);
    setProofProgress(0);
    setIsProofPaused(false);
  };

  const operatingPatterns = cases.operatingPatterns.map((pattern, index) => ({
    ...pattern,
    icon: operatingPatternIcons[index] ?? operatingPatternIcons[0]
  }));
  const gainCards = cases.carousel.cards.map((card, index) => ({
    ...card,
    icon: capabilityIcons[index] ?? capabilityIcons[0]
  }));

  return (
    <div className="min-h-screen bg-background-primary text-text-primary antialiased">
      <Navbar />

      <main>
        <section className="cases-hero-background relative overflow-hidden border-b border-border-muted">
          <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-[520px] text-accent-primary opacity-60 lg:block">
            <svg
              viewBox="0 0 1200 520"
              aria-hidden="true"
              className="h-full w-full"
              preserveAspectRatio="xMidYMid slice"
            >
              <path
                d="M0 392 C190 250 258 450 470 284 C690 112 760 274 1200 98"
                fill="none"
                stroke="currentColor"
                strokeDasharray="6 14"
                strokeOpacity="0.2"
                strokeWidth="1.5"
              />
              <g className="cases-flow-particle">
                <circle r="16" fill="currentColor" opacity="0.16" />
                <circle r="7" fill="currentColor" opacity="0.36" />
                <circle r="3.5" fill="currentColor" />
                <animateMotion
                  path="M0 392 C190 250 258 450 470 284 C690 112 760 274 1200 98"
                  dur="10s"
                  repeatCount="indefinite"
                />
              </g>
            </svg>
          </div>

          <div className="relative z-10 mx-auto grid max-w-7xl gap-10 px-4 pb-16 pt-36 sm:px-6 sm:pb-20 sm:pt-40 lg:grid-cols-[0.96fr_1.04fr] lg:items-center lg:px-8 lg:pb-24 lg:pt-40">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45 }}
            >
              <div className="mb-5 flex flex-wrap gap-2">
                <Badge
                  variant="outline"
                  className="border-accent-primary/30 bg-background-card/70 px-4 py-2 font-mono uppercase tracking-[0.18em] text-accent-primary shadow-lg shadow-shadow backdrop-blur"
                >
                  {cases.heroBadge}
                </Badge>
                <Badge
                  variant="outline"
                  className="gap-2 border-accent-primary/30 bg-background-card/70 px-4 py-2 font-mono uppercase tracking-[0.18em] text-accent-primary shadow-lg shadow-shadow backdrop-blur"
                >
                  <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                  {content.common.soc2Compliant}
                </Badge>
              </div>
              <h1 className="max-w-4xl text-[clamp(2.35rem,5vw,4.8rem)] font-light leading-[1.02] tracking-tight text-text-primary">
                {cases.title}
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-text-secondary sm:text-xl">
                {cases.description}
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button
                  type="button"
                  size="lg"
                  onClick={() => scrollToElementId('case-studies')}
                  className="cursor-pointer gap-2 bg-text-primary px-8 font-mono text-text-inverse hover:bg-text-primary/90"
                >
                  {cases.heroPrimaryCta}
                  <ButtonArrow className="h-5 w-5" />
                </Button>

                {/* CALCULATOR SAVINGS COMMENTED OUT */}
                {/* <Button asChild variant="outline" size="lg">
                  <Link
                    to={localizedPath('/')}
                    state={{ scrollToSection: 'calculator' }}
                    className="gap-2 bg-transparent px-8 font-mono"
                  >
                    {cases.calculateSavings}
                  </Link>
                </Button> */}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08, duration: 0.45 }}
              className="relative overflow-hidden rounded-lg border border-border-primary bg-background-card/55 shadow-xl shadow-shadow backdrop-blur"
            >
              <img
                src="/images/datasliv_light.jpg"
                alt="DataSliv platform interface in light mode"
                className="block aspect-[1.94/1] h-full min-h-[18rem] w-full object-cover object-left-top dark:hidden"
                loading="eager"
              />
              <img
                src="/images/datasliv_dark.jpg"
                alt="DataSliv platform interface in dark mode"
                className="hidden aspect-[1.94/1] h-full min-h-[18rem] w-full object-cover object-left-top dark:block"
                loading="eager"
              />
            </motion.div>
          </div>
        </section>

        <section className="border-b border-border-muted bg-background-primary py-16 sm:py-20">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.72fr_1.28fr] lg:items-center lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.42 }}
            >
              <Badge variant="secondary" className="mb-4">
                {cases.featured.eyebrow}
              </Badge>
              <h2 className="mt-4 text-[clamp(3.5rem,11vw,7.5rem)] font-light leading-none text-accent-primary">
                18x
              </h2>
              <p className="mt-4 max-w-xl text-xl leading-8 text-text-primary">
                {cases.featured.proofPoint}
              </p>
              <p className="mt-3 max-w-xl leading-7 text-text-secondary">
                {cases.featured.selectorLabel}
              </p>
              {/* CALCULATOR SAVINGS COMMENTED OUT */}
              {/* <Button asChild className="mt-7 cursor-pointer gap-2 font-mono">
                <Link
                  to={localizedPath('/')}
                  state={{ scrollToSection: 'calculator' }}
                >
                  {cases.featured.cta}
                  <ButtonArrow className="h-4 w-4" />
                </Link>
              </Button> */}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ delay: 0.08, duration: 0.42 }}
              className="cases-proof-showcase relative grid gap-4"
              onMouseEnter={() => setIsProofPaused(true)}
              onMouseLeave={() => setIsProofPaused(false)}
              onFocusCapture={() => setIsProofPaused(true)}
              onBlurCapture={event => {
                const nextFocusedElement = event.relatedTarget;

                if (
                  !(nextFocusedElement instanceof Node) ||
                  !event.currentTarget.contains(nextFocusedElement)
                ) {
                  setIsProofPaused(false);
                }
              }}
            >
              <div
                className="relative z-10 grid overflow-hidden rounded-lg border border-border-primary bg-background-primary/85 sm:grid-cols-3"
                role="tablist"
                aria-label={cases.featured.selectorAriaLabel}
              >
                {proofSelectorItems.map((item, index) => {
                  const SelectorIcon = item.icon;
                  const isActive = index === activeProofIndex;

                  return (
                    <button
                      key={item.title}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => handleProofSelect(index)}
                      className={cn(
                        'relative flex min-h-20 cursor-pointer items-center justify-center gap-3 px-4 py-5 text-center outline-none transition focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-border-focus',
                        isActive
                          ? 'bg-background-secondary text-text-primary'
                          : 'text-text-secondary hover:bg-background-secondary/60'
                      )}
                    >
                      <SelectorIcon
                        className={cn(
                          'h-5 w-5 shrink-0',
                          isActive ? 'text-accent-primary' : 'text-text-muted'
                        )}
                      />
                      <span className="font-mono text-xs uppercase tracking-[0.18em]">
                        {item.value}
                      </span>
                      {isActive && (
                        <span
                          className="absolute inset-x-0 bottom-0 h-1 bg-accent-primary/15"
                          aria-hidden="true"
                        >
                          <span
                            key={activeProofIndex}
                            className="cases-proof-showcase__timer-line block h-full origin-left bg-accent-primary"
                            style={
                              isActive
                                ? { transform: `scaleX(${proofProgress})` }
                                : undefined
                            }
                          />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="relative z-10 h-[39rem] overflow-hidden border rounded-xl border-border-primary bg-background-primary/90 shadow-sm shadow-shadow sm:h-[36rem] lg:h-[24rem]">
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={activeProofItem?.title ?? activeProofIndex}
                    initial={{ opacity: 0, x: -34 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 34 }}
                    transition={{ duration: 0.34, ease: 'easeOut' }}
                    className="grid h-full grid-rows-[15rem_1fr] lg:grid-cols-[0.95fr_1.05fr] lg:grid-rows-none"
                  >
                    <div className="relative min-h-0 overflow-hidden bg-[#f7fbff] dark:bg-[#020711]">
                      <img
                        src={activeProofVisual.lightSrc}
                        alt={`${activeProofVisual.alt} in light mode`}
                        className="absolute inset-0 h-full w-full scale-110 object-contain object-center dark:hidden"
                        loading="lazy"
                      />
                      <img
                        src={activeProofVisual.darkSrc}
                        alt={`${activeProofVisual.alt} in dark mode`}
                        className="absolute inset-0 hidden h-full w-full scale-110 object-contain object-center dark:block"
                        loading="lazy"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-5">
                        <span className="inline-flex items-center gap-2 rounded-md border border-border-primary bg-background-card/90 px-3 py-2 font-mono text-xs uppercase tracking-[0.16em] text-accent-primary shadow-sm shadow-shadow backdrop-blur">
                          <ActiveProofIcon className="h-4 w-4" />
                          {activeProofItem?.value}
                        </span>
                      </div>
                    </div>

                    <div className="flex min-h-0 flex-col justify-center overflow-hidden p-6 sm:p-8">
                      <p className="font-mono text-xs uppercase tracking-[0.18em] text-accent-primary">
                        {cases.outcomeOverview.title}
                      </p>
                      <h3 className="mt-5 text-3xl font-light leading-tight text-text-primary sm:text-4xl">
                        {activeProofItem?.title}
                      </h3>
                      <p className="mt-5 text-base leading-7 text-text-secondary sm:text-lg sm:leading-8">
                        {activeProofItem?.description}
                      </p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>
          </div>
        </section>

        <section
          id="case-studies"
          data-scroll-offset="112"
          className="bg-background-secondary py-16 sm:py-20"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <Badge variant="secondary" className="mb-4">
                  {cases.sectionBadge}
                </Badge>
                <h2 className="text-4xl font-light tracking-tight sm:text-5xl">
                  {cases.sectionTitle}
                </h2>
              </div>
              <p className="max-w-xl leading-7 text-text-secondary">
                <span className="mb-2 block font-mono text-xs uppercase tracking-[0.18em] text-accent-primary">
                  {cases.workflowTitle}
                </span>
                {cases.workflowDescription}
              </p>
            </div>

            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {caseCards.map((card, index) => {
                const CaseIcon = card.visual.icon;
                const hasLogo = Boolean(card.visual.logoSrc);

                return (
                  <motion.article
                    key={card.title}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ delay: index * 0.05, duration: 0.42 }}
                    className={cn(
                      'rounded-lg border bg-background-card p-6 shadow-sm shadow-shadow',
                      index === 0
                        ? 'border-accent-primary/45'
                        : 'border-border-primary'
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-44 items-center justify-center overflow-hidden rounded-md border sm:h-52',
                        hasLogo
                          ? 'border-white/10 bg-[#07111d]'
                          : 'border-border-muted bg-background-secondary'
                      )}
                    >
                      {card.visual.logoSrc ? (
                        <img
                          src={card.visual.logoSrc}
                          alt={card.visual.logoAlt ?? `${card.title} logo`}
                          className={cn(
                            'h-full w-full object-contain',
                            card.visual.logoClassName
                          )}
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex flex-col items-center gap-3 text-center">
                          <span className="relative flex h-20 w-20 items-center justify-center rounded-md bg-accent-primary/10 text-accent-primary">
                            <span className="cases-pulse absolute h-7 w-7 rounded-full bg-accent-primary/30" />
                            <CaseIcon className="relative h-10 w-10" />
                          </span>
                          <span className="max-w-[14rem] px-2 font-mono text-xs uppercase tracking-[0.16em] text-text-muted">
                            {card.eyebrow}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      <span className="rounded-full border border-border-primary bg-background-secondary px-3 py-1 font-mono text-xs text-text-secondary">
                        {card.eyebrow}
                      </span>
                      {index === 0 && (
                        <span className="rounded-full border border-accent-primary/30 bg-accent-primary/10 px-3 py-1 font-mono text-xs text-accent-primary">
                          18x
                        </span>
                      )}
                    </div>

                    <h3 className="mt-4 text-2xl font-light">{card.title}</h3>
                    <p className="mt-3 leading-7 text-text-secondary">
                      {card.summary}
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {card.outcomes.map(outcome => (
                        <span
                          key={outcome}
                          className="rounded-full border border-border-primary bg-background-secondary px-3 py-1 font-mono text-xs text-text-secondary"
                        >
                          {outcome}
                        </span>
                      ))}
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="border-y border-border-muted bg-background-primary py-16 sm:py-20">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.84fr_1.16fr] lg:items-start lg:px-8">
            <div>
              <Badge variant="secondary" className="mb-4">
                {cases.patternBadge}
              </Badge>
              <h2 className="text-4xl font-light tracking-tight sm:text-5xl">
                {cases.patternTitle}
              </h2>
              <p className="mt-4 max-w-2xl leading-7 text-text-secondary">
                {cases.patternDescription}
              </p>
            </div>

            <div className="grid gap-4">
              {operatingPatterns.map(pattern => {
                const PatternIcon = pattern.icon;

                return (
                  <motion.article
                    key={pattern.title}
                    initial={{ opacity: 0, x: 18 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.42 }}
                    className="rounded-lg border border-border-primary bg-background-card p-5 shadow-sm shadow-shadow"
                  >
                    <div className="flex gap-4">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-primary/10 text-accent-primary">
                        <PatternIcon className="h-5 w-5" />
                      </span>
                      <div>
                        <h3 className="font-semibold text-text-primary">
                          {pattern.title}
                        </h3>
                        <p className="mt-2 text-sm leading-6 text-text-secondary">
                          {pattern.description}
                        </p>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-background-secondary py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <Badge variant="secondary" className="mb-4">
                {cases.capabilityBadge}
              </Badge>
              <div className="flex items-center gap-3">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border-primary bg-background-card/70 text-text-muted"
                  aria-hidden="true"
                >
                  <MousePointerClick className="h-4 w-4" />
                </span>
                <h2 className="text-4xl font-light tracking-tight sm:text-5xl">
                  {cases.carousel.title}
                </h2>
              </div>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-5">
              {gainCards.map((card, index) => {
                const GainIcon = card.icon;

                return (
                  <motion.div
                    key={card.title}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ delay: index * 0.04, duration: 0.42 }}
                    className="group relative h-[17rem] hover:z-20 focus-within:z-20"
                  >
                    <article
                      tabIndex={0}
                      className="cases-gain-card absolute inset-x-0 top-0 overflow-hidden rounded-lg border border-border-primary bg-background-card p-5 shadow-sm shadow-shadow outline-none group-hover:border-accent-primary/45 group-hover:shadow-xl group-focus-within:border-accent-primary/45 group-focus-within:shadow-xl focus-visible:ring-2 focus-visible:ring-border-focus"
                    >
                      <div className="flex h-full flex-col">
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-accent-primary/10 text-accent-primary transition-colors duration-300 group-hover:bg-accent-primary/15 group-focus-within:bg-accent-primary/15">
                          <GainIcon className="h-6 w-6" />
                        </span>
                        <h3 className="mt-6 text-lg font-light leading-tight text-text-primary">
                          {card.title}
                        </h3>
                        <p className="cases-gain-card__copy mt-4 text-sm leading-6 text-text-secondary">
                          {card.description}
                        </p>
                      </div>
                    </article>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-background-primary py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-8 border-y border-border-primary py-10 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <h2 className="max-w-3xl text-3xl font-light tracking-tight sm:text-4xl">
                  {cases.finalCta.title}
                </h2>
                <p className="mt-4 max-w-2xl leading-7 text-text-secondary">
                  {cases.finalCta.description}
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row lg:justify-end">
                {/* CALCULATOR SAVINGS COMMENTED OUT */}
                {/* <Button asChild className="gap-2 font-mono">
                  <Link
                    to={localizedPath('/')}
                    state={{ scrollToSection: 'calculator' }}
                  >
                    {cases.estimateCost}
                    <ButtonArrow className="h-4 w-4" />
                  </Link>
                </Button> */}
                <Button
                  asChild
                  variant="outline"
                  className="gap-2 bg-transparent font-mono"
                >
                  <Link to={localizedPath('/contact')}>{cases.bookDemo}</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CasesPage;
